/**
 * The app-side mutation service (WYSIWYG plan §2/§5): typed model ops from
 * board/inspector gestures → engine synthesis → EditApplier → flushed
 * recompile. Owns the two app-level invariants the engine can't:
 *
 *  - **Generation guard.** Spans are only valid against the exact text their
 *    compile saw. A mutation is refused as `"stale"` when the live store text
 *    for the target file differs from `compiledSources` (user mid-typing —
 *    the debounced recompile hasn't caught up). Callers retry after the next
 *    compile; ops are user-paced, so no queue beyond that.
 *  - **Journal undo.** Every applied mutation records `{path, before, after}`
 *    per touched file. `undo()` re-applies the inverse as a NEW edit through
 *    the same funnel (uniform across open/closed files); a stale entry
 *    (current text ≠ `after`) is dropped, never force-applied.
 */

import { useContext, useMemo } from "react";
import {
  appendTopLevelModuleEdit,
  computeMoveEdits,
  scaffoldViewEdit,
  synthesizeViewMutation,
  createSpanLookup,
  promoteStubWithMember,
  scaffoldProcessEdit,
  scaffoldTestEdit,
  synthesizeInterfaceDelete,
  synthesizeFlowBodyMutation,
  synthesizeModuleMutation,
  synthesizeProcessMutation,
  synthesizeProcessStepMutation,
  MutationRefusedError,
  type FlowBodyMutation,
  type ModuleMutation,
  type ProcessModel,
  type ProcessMutation,
  type ProcessStepMutation,
  type ResolvedModel,
  type SpanLookup,
  type TextEdit,
  type ViewMutation,
} from "@archlang/engine";
import type { Vfs } from "@archlang/viewer-core";
import { applyEditsToFile } from "./editApplier.js";
import { useWorkspace, WorkspaceContext } from "./WorkspaceContext.js";
import { useWorkspaceModel, WorkspaceModelContext } from "./WorkspaceModelContext.js";
import type { WorkspaceStore } from "./WorkspaceStore.js";

export type MutationResult =
  | { readonly status: "applied"; readonly route: "monaco" | "store" }
  | { readonly status: "stale" } // compile hasn't caught up with the store text
  | { readonly status: "refused"; readonly reason: string; readonly message: string }
  | { readonly status: "unknown-target" };

interface JournalFileDelta {
  readonly path: string; // store-keyed
  readonly before: string;
  readonly after: string;
}

interface JournalEntry {
  readonly files: readonly JournalFileDelta[];
}

/** Pre-synthesized offset edits for one file, e.g. from an LSP WorkspaceEdit.
 *  `expectedText` is the text the edits were computed against — the guard. */
export interface ExternalFileEdits {
  readonly path: string; // absolute ("/"-prefixed) or store-keyed
  readonly expectedText: string;
  readonly edits: readonly TextEdit[];
}

export interface ModelMutationsApi {
  /** Synthesize + apply one op on a module target (board/wire id). */
  mutateModule(targetId: string, op: ModuleMutation): MutationResult;
  /** §4a stub promotion: author the stub chain, optionally carrying a member
   *  line (the value op that triggered it). */
  promoteStub(targetId: string, memberLine?: string): MutationResult;
  /**
   * Apply externally-computed edits (LSP rename WorkspaceEdit → offsets) as
   * ONE journaled mutation. All-or-nothing: every file's live text must still
   * equal `expectedText` (the async round-trip guard, plan §6) or the whole
   * batch is refused as stale.
   */
  applyExternalEdits(files: readonly ExternalFileEdits[]): MutationResult;
  /** drawEdge into an EXISTING process (W2, §3.3 chooser steps 1–2). */
  mutateProcess(processId: string, op: ProcessMutation): MutationResult;
  /** drawEdge via "new process" (§3.3 step 2): scaffold a process carrying
   *  the step — nested in `anchorModuleId`'s body when given (common
   *  ancestor), else appended at the top level of `fileOfModuleId`'s file. */
  addProcessWithStep(args: {
    readonly name: string | null; // null = zero-prompt anonymous
    readonly anchorModuleId: string | null;
    readonly fileOfModuleId: string; // origin: the from-module (its file hosts a root append)
    readonly from: string;
    readonly to: string;
  }): MutationResult;
  /** P8 "Save as test": append a pre-serialized `test` block (from `printTest`)
   *  at the top level of the process's own file. */
  addTest(args: { readonly processId: string; readonly testText: string }): MutationResult;
  /** Delete an interface declaration (dotted path id, e.g. `Orders.place`). */
  deleteInterface(path: string): MutationResult;
  /** Root-level module add (§3.4): appended at the end of `absFilePath`. */
  addTopLevelModule(args: {
    readonly absFilePath: string;
    readonly kind: string;
    readonly name?: string;
    readonly braced?: boolean;
  }): MutationResult;
  /** W3: move a module under a new parent (null = its file's root), with
   *  cross-file reference requalification. Multi-file all-or-nothing. */
  moveModule(targetId: string, newParentId: string | null): MutationResult;
  /** W3: step ops on the process IR (stepId-keyed). */
  mutateProcessStep(processId: string, m: ProcessStepMutation): MutationResult;
  /**
   * Apply SEVERAL step ops to one process as a single journaled mutation (the
   * multi-select delete path, P4 #28). Every op is synthesized against the SAME
   * compiled snapshot and the edits merged, then applied once — a synchronous
   * per-op loop can't work because `flush()` recompiles only on the next render,
   * so op #2 would see the pre-edit compiled text and refuse `stale`. One undo
   * reverts the whole batch. Ops whose merged edit would OVERLAP an earlier op's
   * (two `;`-separated siblings on one line) are dropped from the batch — the
   * survivors still apply, nothing is corrupted. Refused/unknown short-circuit
   * the whole batch (all-or-nothing on a hard failure).
   */
  mutateProcessSteps(processId: string, ops: readonly ProcessStepMutation[]): MutationResult;
  /** View builder: persist a (draft) view into `absFilePath` with its full
   *  clause list — the "save to model" half of the two-tier flow. */
  scaffoldView(args: {
    readonly absFilePath: string;
    readonly name: string;
    readonly clauses: readonly string[];
  }): MutationResult;
  /** View builder: mutate a MODEL view (by name or `#id`). */
  mutateView(idOrName: string, m: ViewMutation): MutationResult;
  /** Several view ops as ONE write. Each op is synthesized against the same
   *  compiled text and the edits apply together, so a save that touches two
   *  disjoint regions of a view (e.g. the knob header AND the clause list)
   *  lands atomically — issuing them as two calls would synthesize the second
   *  against spans the first already moved. */
  mutateViewBatch(idOrName: string, ms: readonly ViewMutation[]): MutationResult;
  /** BPMN lane/pool rail edit: mutate the `lane`/`pool` clause list of the
   *  flow clause at `flowClauseIndex` (in the view's clause list). */
  mutateFlowBody(idOrName: string, flowClauseIndex: number, m: FlowBodyMutation): MutationResult;
  /** Inverse-apply the last mutation (drops silently when stale). */
  undo(): boolean;
  /** Entries currently undoable (journal depth). */
  undoDepth(): number;
}

/** The compiler resolves an absolute ("/"-prefixed) VFS; the store may key
 *  bare (Studio). Translate a compiled file key to the store's key. */
function storeKeyFor(store: WorkspaceStore, absFile: string): string | undefined {
  const files = store.getState().files;
  if (files.has(absFile)) return absFile;
  const bare = absFile.startsWith("/") ? absFile.slice(1) : absFile;
  if (files.has(bare)) return bare;
  return undefined;
}

/** True when a candidate edit's replaced span `[start, end)` intersects any
 *  span already collected — a batch of step edits must stay non-overlapping
 *  (`applyTextEdits` throws otherwise). Adjacent (`end === next.start`, e.g. two
 *  whole-line removals) does NOT count as overlap. */
function overlapsAny(collected: readonly TextEdit[], e: TextEdit): boolean {
  return collected.some((c) => e.start < c.end && c.start < e.end);
}

/** The compile slice the service needs — the hook feeds it from the shared
 *  workspace model; tests feed it from a headless engine pipeline. */
export interface MutationCompileState {
  readonly resolvedModel: ResolvedModel | null;
  readonly compiledSources: Vfs | null;
  readonly flush: () => void;
}

// ONE undo journal per store, shared by every service instance (board keys,
// sidebar bundle, F2 rename all journal here) — fragmented per-hook journals
// made board-⌘Z skip ops committed through another hook's instance.
const journalByStore = new WeakMap<object, JournalEntry[]>();

function journalFor(store: object): JournalEntry[] {
  let j = journalByStore.get(store);
  if (!j) {
    j = [];
    journalByStore.set(store, j);
  }
  return j;
}

export function useModelMutations(): ModelMutationsApi {
  const store = useWorkspace();
  const shared = useWorkspaceModel();
  return useMemo(
    () => createModelMutations(store, shared, journalFor(store)),
    [store, shared],
  );
}

/** Like {@link useModelMutations} but `null` outside a Workspace(Model)
 *  provider — for surfaces (the view table/matrix containers) that render
 *  read-only in provider-less compositions instead of throwing. */
export function useModelMutationsOptional(): ModelMutationsApi | null {
  const store = useContext(WorkspaceContext);
  const shared = useContext(WorkspaceModelContext);
  return useMemo(
    () => (store && shared ? createModelMutations(store, shared, journalFor(store)) : null),
    [store, shared],
  );
}

export function createModelMutations(
  store: WorkspaceStore,
  shared: MutationCompileState,
  journalStore?: JournalEntry[],
): ModelMutationsApi {
  const journal = { current: journalStore ?? [] };

  // Rebuilt per compile; the lookup lazily indexes on first use.
  const lookup: SpanLookup | null = shared.resolvedModel
    ? createSpanLookup(shared.resolvedModel)
    : null;

  const applyToFile = (absFile: string, edits: readonly TextEdit[]): MutationResult => {
      const storeKey = storeKeyFor(store, absFile);
      if (storeKey === undefined) return { status: "unknown-target" };

      const live = store.getState().files.get(storeKey)!;
      const compiled = shared.compiledSources?.get(absFile);
      if (compiled === undefined || compiled !== live) return { status: "stale" };

      const before = live;
      const route = applyEditsToFile(store, storeKey, edits);
      const after = store.getState().files.get(storeKey) ?? before;
      journal.current.push({ files: [{ path: storeKey, before, after }] });
      shared.flush();
      return { status: "applied", route };
  };

  const run = (absFile: string | undefined, synth: () => readonly TextEdit[]): MutationResult => {
    if (!absFile) return { status: "unknown-target" };
    try {
      // Synthesize against the COMPILED text (what the spans index), then
      // apply — the guard inside applyToFile rejects if the store moved on.
      return applyToFile(absFile, synth());
    } catch (err) {
      if (err instanceof MutationRefusedError) {
        return { status: "refused", reason: err.reason, message: err.message };
      }
      throw err;
    }
  };

  // Resolve a process handle (`#id` / qualified / bare name) to its file + the
  // compiled source + the ProcessModel — shared by the single + batch step ops.
  type ProcessResolution =
    | { readonly err: MutationResult; readonly absFile?: undefined; readonly src?: undefined; readonly pm?: undefined }
    | { readonly err?: undefined; readonly absFile: string; readonly src: string; readonly pm: ProcessModel };
  const resolveProcess = (processId: string): ProcessResolution => {
    const model = shared.resolvedModel;
    if (!lookup || !model || !shared.compiledSources) return { err: { status: "stale" } };
    const loc = lookup.processFor(processId);
    if (!loc) return { err: { status: "unknown-target" } };
    const raw = processId.startsWith("#") ? processId.slice(1) : processId;
    const pm = (model.processModels ?? []).find((p) => {
      if (p.id && (p.id as string) === raw) return true;
      const qualified = [...(p.ownerPath ?? []), p.name].join(".");
      return qualified === raw || p.name === raw;
    });
    if (!pm) return { err: { status: "unknown-target" } };
    const absFile = loc.file;
    const src = absFile ? shared.compiledSources.get(absFile) : undefined;
    if (absFile === undefined || src === undefined) return { err: { status: "unknown-target" } };
    return { absFile, src, pm };
  };

  return {
      mutateModule(targetId, op) {
        if (!lookup || !shared.compiledSources) return { status: "stale" };
        const loc = lookup.moduleFor(targetId);
        if (!loc) return { status: "unknown-target" };
        const absFile = loc.file;
        const src = absFile ? shared.compiledSources.get(absFile) : undefined;
        if (absFile === undefined || src === undefined) return { status: "unknown-target" };
        return run(absFile, () => synthesizeModuleMutation(src, loc, op));
      },

      promoteStub(targetId, memberLine) {
        const model = shared.resolvedModel;
        if (!lookup || !model || !shared.compiledSources) return { status: "stale" };
        const loc = lookup.moduleFor(targetId);
        if (!loc) return { status: "unknown-target" };
        const absFile = loc.file;
        const src = absFile ? shared.compiledSources.get(absFile) : undefined;
        if (absFile === undefined || src === undefined) return { status: "unknown-target" };
        return run(absFile, () => promoteStubWithMember(src, model, targetId, memberLine));
      },

    mutateProcess(processId, op) {
      if (!lookup || !shared.compiledSources) return { status: "stale" };
      const loc = lookup.processFor(processId);
      if (!loc) return { status: "unknown-target" };
      const absFile = loc.file;
      const src = absFile ? shared.compiledSources.get(absFile) : undefined;
      if (absFile === undefined || src === undefined) return { status: "unknown-target" };
      return run(absFile, () => synthesizeProcessMutation(src, loc, op));
    },

    addProcessWithStep({ name, anchorModuleId, fileOfModuleId, from, to }) {
      if (!lookup || !shared.compiledSources) return { status: "stale" };
      const origin = lookup.moduleFor(fileOfModuleId);
      if (!origin?.file) return { status: "unknown-target" };
      // Nest into the common ancestor only when it is authored and lives in
      // the SAME file as the origin (else fall back to a top-level append).
      const anchorLoc = anchorModuleId ? lookup.moduleFor(anchorModuleId) : null;
      const anchor =
        anchorLoc && anchorLoc.materialization !== "stub" && anchorLoc.file === origin.file
          ? anchorLoc.decl
          : null;
      const absFile = origin.file;
      const src = shared.compiledSources.get(absFile);
      if (src === undefined) return { status: "unknown-target" };
      return run(absFile, () => scaffoldProcessEdit(src, anchor, name, from, to));
    },

    addTest({ processId, testText }) {
      if (!lookup || !shared.compiledSources) return { status: "stale" };
      const loc = lookup.processFor(processId);
      const absFile = loc?.file;
      const src = absFile ? shared.compiledSources.get(absFile) : undefined;
      if (!loc || absFile === undefined || src === undefined) return { status: "unknown-target" };
      // A `test` decl is legal at top level (§5) — the engine primitive owns the
      // root-append + blank-line separator (single source of truth).
      return run(absFile, () => scaffoldTestEdit(src, testText));
    },

    deleteInterface(path) {
      if (!lookup || !shared.compiledSources) return { status: "stale" };
      const loc = lookup.interfaceFor(path);
      if (!loc) return { status: "unknown-target" };
      const absFile = loc.file;
      const src = absFile ? shared.compiledSources.get(absFile) : undefined;
      if (absFile === undefined || src === undefined) return { status: "unknown-target" };
      return run(absFile, () => synthesizeInterfaceDelete(src, loc));
    },

    addTopLevelModule({ absFilePath, kind, name, braced }) {
      if (!shared.compiledSources) return { status: "stale" };
      const abs = absFilePath.startsWith("/") ? absFilePath : "/" + absFilePath;
      // Declarations belong in .arch sources only — the compiled VFS also
      // carries .md documents and the manifest, which must never receive one.
      if (!abs.endsWith(".arch")) return { status: "unknown-target" };
      const src = shared.compiledSources.get(abs);
      if (src === undefined) return { status: "unknown-target" };
      return run(abs, () => appendTopLevelModuleEdit(src, kind, name, braced));
    },

    moveModule(targetId, newParentId) {
      const model = shared.resolvedModel;
      if (!model || !shared.compiledSources) return { status: "stale" };
      let byFile: Map<string, TextEdit[]>;
      try {
        byFile = computeMoveEdits(model, shared.compiledSources, targetId, newParentId);
      } catch (err) {
        if (err instanceof MutationRefusedError) {
          return { status: "refused", reason: err.reason, message: err.message };
        }
        throw err;
      }
      // Multi-file all-or-nothing, mirrored from applyExternalEdits: verify
      // EVERY touched file against the compiled snapshot before touching any.
      const resolved: { storeKey: string; before: string; edits: readonly TextEdit[] }[] = [];
      for (const [absFile, edits] of byFile) {
        const storeKey = storeKeyFor(store, absFile);
        if (storeKey === undefined) return { status: "unknown-target" };
        const live = store.getState().files.get(storeKey)!;
        if (live !== shared.compiledSources.get(absFile)) return { status: "stale" };
        resolved.push({ storeKey, before: live, edits });
      }
      let route: "monaco" | "store" = "store";
      const deltas: JournalFileDelta[] = [];
      for (const r of resolved) {
        const rt = applyEditsToFile(store, r.storeKey, r.edits);
        if (rt === "monaco") route = "monaco";
        deltas.push({
          path: r.storeKey,
          before: r.before,
          after: store.getState().files.get(r.storeKey) ?? r.before,
        });
      }
      journal.current.push({ files: deltas });
      shared.flush();
      return { status: "applied", route };
    },

    mutateProcessStep(processId, m) {
      const resolved = resolveProcess(processId);
      if (resolved.err) return resolved.err;
      const { absFile, src, pm } = resolved;
      return run(absFile, () => synthesizeProcessStepMutation(src, pm, m));
    },

    mutateProcessSteps(processId, ops) {
      if (ops.length === 0) return { status: "unknown-target" };
      const resolved = resolveProcess(processId);
      if (resolved.err) return resolved.err;
      const { absFile, src, pm } = resolved;
      return run(absFile, () => {
        // Synthesize each op against the ONE compiled src and merge. A later op
        // whose edit would overlap an already-collected span is DROPPED (never
        // corrupt): the two `;`-siblings-on-a-line case; the earliest wins. A
        // synthesizer that THROWS (refused / no-span) propagates and aborts the
        // whole batch via `run`'s catch — all-or-nothing on a hard failure.
        const merged: TextEdit[] = [];
        for (const op of ops) {
          const edits = synthesizeProcessStepMutation(src, pm, op);
          if (edits.some((e) => overlapsAny(merged, e))) continue;
          merged.push(...edits);
        }
        return merged;
      });
    },

    scaffoldView({ absFilePath, name, clauses }) {
      if (!shared.compiledSources) return { status: "stale" };
      const abs = absFilePath.startsWith("/") ? absFilePath : "/" + absFilePath;
      if (!abs.endsWith(".arch")) return { status: "unknown-target" };
      const src = shared.compiledSources.get(abs);
      if (src === undefined) return { status: "unknown-target" };
      return run(abs, () => scaffoldViewEdit(src, null, name, clauses));
    },

    mutateView(idOrName, m) {
      if (!lookup || !shared.compiledSources) return { status: "stale" };
      const view = lookup.viewFor(idOrName);
      if (!view) return { status: "unknown-target" };
      const absFile = view.source?.file;
      const src = absFile ? shared.compiledSources.get(absFile) : undefined;
      if (absFile === undefined || src === undefined) return { status: "unknown-target" };
      // The model rides along so style-subject ops refuse an unaddressable
      // subject (unparseable text / a ref resolving to no node) instead of
      // writing it — the engine-side last line of defense against corruption.
      return run(absFile, () => synthesizeViewMutation(src, view, m, shared.resolvedModel));
    },

    mutateViewBatch(idOrName, ms) {
      if (!lookup || !shared.compiledSources) return { status: "stale" };
      const view = lookup.viewFor(idOrName);
      if (!view) return { status: "unknown-target" };
      const absFile = view.source?.file;
      const src = absFile ? shared.compiledSources.get(absFile) : undefined;
      if (absFile === undefined || src === undefined) return { status: "unknown-target" };
      return run(absFile, () =>
        ms.flatMap((m) => synthesizeViewMutation(src, view, m, shared.resolvedModel)),
      );
    },

    mutateFlowBody(idOrName, flowClauseIndex, m) {
      if (!lookup || !shared.compiledSources) return { status: "stale" };
      const view = lookup.viewFor(idOrName);
      if (!view) return { status: "unknown-target" };
      const flow = view.clauses[flowClauseIndex];
      if (!flow || flow.kind !== "flow") return { status: "unknown-target" };
      const absFile = view.source?.file;
      const src = absFile ? shared.compiledSources.get(absFile) : undefined;
      if (absFile === undefined || src === undefined) return { status: "unknown-target" };
      return run(absFile, () => synthesizeFlowBodyMutation(src, flow, m));
    },

    applyExternalEdits(files) {
      if (files.length === 0) return { status: "unknown-target" };
      // Resolve + guard EVERY file before touching any (all-or-nothing).
      const resolved: { storeKey: string; before: string; edits: readonly TextEdit[] }[] = [];
      for (const f of files) {
        const storeKey = storeKeyFor(store, f.path);
        if (storeKey === undefined) return { status: "unknown-target" };
        const live = store.getState().files.get(storeKey)!;
        if (live !== f.expectedText) return { status: "stale" };
        resolved.push({ storeKey, before: live, edits: f.edits });
      }
      let route: "monaco" | "store" = "store";
      const deltas: JournalFileDelta[] = [];
      for (const r of resolved) {
        const rt = applyEditsToFile(store, r.storeKey, r.edits);
        if (rt === "monaco") route = "monaco";
        deltas.push({
          path: r.storeKey,
          before: r.before,
          after: store.getState().files.get(r.storeKey) ?? r.before,
        });
      }
      journal.current.push({ files: deltas });
      shared.flush();
      return { status: "applied", route };
    },

    undo() {
      const entry = journal.current.pop();
      if (!entry) return false;
      // All-or-nothing: ONE stale file drops the whole entry — partially
      // undoing a cross-file rename would tear it (declaration reverted,
      // references keeping the new name).
      for (const f of entry.files) {
        if (store.getState().files.get(f.path) !== f.after) return false;
      }
      for (const f of entry.files) {
        applyEditsToFile(store, f.path, [
          { start: 0, end: f.after.length, newText: f.before },
        ]);
      }
      shared.flush();
      return true;
    },

    undoDepth() {
      return journal.current.length;
    },
  };
}
